import React, { useEffect, useState } from "react";
import {
  InformationCircleIcon,
  CheckCircleIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import AddDSPModal from "../../modal/AddDSPModal";
import EditDSPModal from "../../modal/EditDSPModal";
import { AddDSPData, DSPData, EditDSPData } from "@/components/types/types";
import { dspApi } from "../../../repositories/dsp";
import Swal from "sweetalert2";
import { formatDSPName } from "../../../utils/formatDSP";
import { PlusIcon } from "lucide-react";
import { formatDisplayDate } from "../../../DashboardPage/utils/dates";

// List of supported DSPs
const SUPPORTED_DSPS = [
  "Spotify for Artists",
  "Amazon Music for Artists",
  "Deezer for Creators",
  // "Apple Music for Artists",
  "Audio Mack for Creators",
];

const DSPs = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedDsp, setSelectedDsp] = useState<string | null>(null);
  const [dspList, setDspList] = useState<DSPData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentDsp, setCurrentDsp] = useState<DSPData | null>(null);
  const [expandedPlatforms, setExpandedPlatforms] = useState<Set<string>>(
    new Set()
  );

  const fetchDSPs = async () => {
    setLoading(true);
    try {
      const response = await dspApi.getDSPs();
      setDspList(response.data.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setDspList([]);
      } else {
        Swal.fire(
          "Error",
          error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDSPs();
  }, []);

  // Format DSP display name to the API-friendly format
  const formatDspTypeForApi = (dspName: string): string => {
    return dspName
      .toLowerCase()
      .replace(/\s+for\s+(artists|creators)$/i, "")
      .replace(/\s+/g, "_");
  };

  // Get all connected accounts for a specific DSP type
  const getConnectedAccounts = (dspType: string): DSPData[] => {
    const formattedType = formatDspTypeForApi(dspType);
    return dspList.filter((dsp) => dsp.type === formattedType);
  };

  // Toggle table visibility for a platform
  const togglePlatformTable = (dspName: string) => {
    const newExpanded = new Set(expandedPlatforms);
    if (newExpanded.has(dspName)) {
      newExpanded.delete(dspName);
    } else {
      newExpanded.add(dspName);
    }
    setExpandedPlatforms(newExpanded);
  };

  const handleConnect = (dspName: string) => {
    const formattedDspName = formatDspTypeForApi(dspName);
    setSelectedDsp(formattedDspName);
    setIsAddModalOpen(true);
  };

  const handleEditClick = (dsp: DSPData) => {
    setCurrentDsp(dsp);
    setIsEditModalOpen(true);
  };

  const handleDeleteDsp = async (dspId: string, dspType: string) => {
    try {
      const dspIdNumber = Number(dspId);
      if (isNaN(dspIdNumber)) throw new Error("Invalid DSP ID");

      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        await dspApi.deleteDSP(dspIdNumber, dspType);
        setDspList((prev) =>
          prev.filter((dsp) => Number(dsp.id) !== dspIdNumber)
        );
        Swal.fire(
          "Deleted!",
          "Your DSP connection has been deleted.",
          "success"
        );
      }
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "An unknown error occurred",
        "error"
      );
    }
  };

  const handleAddDSP = async (dspData: AddDSPData): Promise<void> => {
    try {
      await dspApi.addDSP(dspData);
      Swal.fire("Success", "DSP connected successfully", "success");
      setIsAddModalOpen(false);
      fetchDSPs();
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "An unknown error occurred",
        "error"
      );
    }
  };

  const handleEditDsp = async (dspData: EditDSPData): Promise<void> => {
    try {
      if (currentDsp) {
        await dspApi.updateDSP(currentDsp.id, dspData);
        Swal.fire("Success", "DSP updated successfully", "success");
        setIsEditModalOpen(false);
        setCurrentDsp(null);
        fetchDSPs();
      }
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error.message ||
        "An unknown error occurred",
        "error"
      );
    }
  };

  return (
    <div className="max-w-5xl p-4 bg-white rounded-lg shadow-sm sm:p-6">
      <h2 className="mb-4 text-base font-semibold text-gray-800 sm:text-lg">
        Connected DSP Accounts
      </h2>
      <hr className="mb-4 border-gray-200 sm:mb-6" />

      {/* Explanation Section */}
      <div className="flex items-start p-3 mb-4 rounded-md bg-gray-50 max-sm:p-2 sm:mb-6">
        <InformationCircleIcon className="flex-shrink-0 w-5 h-5 mr-2 text-blue-600 sm:w-6 sm:h-6" />
        <div>
          <p className="text-sm text-gray-600 max-sm:text-xs">
            Connect to DSPs to fetch streaming data and improve pitch targeting.{" "}
          </p>
          <p className="text-sm text-gray-600 max-sm:text-xs">
            Connections refresh automatically every 7 days.{" "}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-8 h-8 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
        </div>
      ) : (
        /* DSP Platforms */
        <div className="space-y-6">
          {SUPPORTED_DSPS.map((dspName) => {
            const connectedAccounts = getConnectedAccounts(dspName);
            const accountCount = connectedAccounts.length;
            const isExpanded = expandedPlatforms.has(dspName);

            return (
              <div
                key={dspName}
                className="overflow-hidden border border-gray-200 rounded-lg"
              >
                {/* Platform Header */}
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-base font-semibold text-gray-800 sm:text-lg">
                        {dspName}
                      </h3>
                      {accountCount > 0 && (
                        <span className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                          {accountCount} account{accountCount !== 1 ? "s" : ""}{" "}
                          connected
                        </span>
                      )}
                      {accountCount === 0 && (
                        <span className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-full">
                          Not connected
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Add Account Button */}
                      <button
                        onClick={() => handleConnect(dspName)}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 transition-colors rounded-md bg-blue-50 hover:bg-blue-100"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Connect
                      </button>

                      {/* View Accounts Button */}
                      {accountCount > 0 && (
                        <button
                          onClick={() => togglePlatformTable(dspName)}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 transition-colors bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          <EyeIcon className="w-4 h-4" />
                          {isExpanded ? "Hide" : "View"} Accounts
                          {isExpanded ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Accounts Table */}
                {accountCount > 0 && isExpanded && (
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="px-4 py-3 font-medium text-gray-900">
                              #
                            </th>
                            <th className="px-4 py-3 font-medium text-gray-900">
                              Name
                            </th>
                            <th className="px-4 py-3 font-medium text-gray-900">
                              Email
                            </th>
                            <th className="px-4 py-3 font-medium text-gray-900">
                              Connected Date
                            </th>
                            <th className="px-4 py-3 font-medium text-gray-900">
                              Status
                            </th>
                            <th className="px-4 py-3 font-medium text-center text-gray-900">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {connectedAccounts.map((account, index) => (
                            <tr
                              key={account.id}
                              className="transition-colors hover:bg-gray-50"
                            >
                              <td className="px-4 py-3 text-gray-600">
                                {index + 1}
                              </td>
                              <td className="px-2 py-2">
                                  <div className="flex flex-wrap gap-1">
                                    {account.name
                                      .split(',')
                                      .filter(artist => artist.trim() !== '')
                                      .map((artist, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-block px-3 py-1 text-sm font-medium rounded-full bg-sky-100 text-grey-900"
                                          aria-label={`Artist: ${artist.trim()}`}
                                        >
                                          {artist.trim()}
                                        </span>
                                      ))}
                                  </div>
                                
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-gray-600">
                                  {account.email}
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <div className="text-gray-600">
                                  {formatDisplayDate(account.createdAt)}
                                </div>
                              </td>

                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                  <CheckCircleIcon className="w-3 h-3" />
                                  Active
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-1">
                                  {/* Edit Button */}
                                  <button
                                    onClick={() => handleEditClick(account)}
                                    className="p-2 text-blue-600 transition-colors rounded-md hover:bg-blue-50"
                                    title="Edit account"
                                  >
                                    <PencilIcon className="w-4 h-4" />
                                  </button>

                                  {/* Delete Button */}
                                  <button
                                    onClick={() =>
                                      handleDeleteDsp(account.id, account.type)
                                    }
                                    className="p-2 text-red-600 transition-colors rounded-md hover:bg-red-50"
                                    title="Delete account"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* No Accounts Message */}
                {accountCount === 0 && (
                  <div className="p-6 text-center text-gray-500">
                    <div className="mb-2">
                      <InformationCircleIcon className="w-12 h-12 mx-auto text-gray-300" />
                    </div>
                    <p className="mb-1 text-sm font-medium">
                      No accounts connected
                    </p>
                    <p className="text-xs">
                      Connect your {dspName} account to get started with data
                      syncing.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      {!loading && dspList.length > 0 && (
        <div className="p-4 mt-6 border border-blue-200 rounded-lg bg-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-blue-800">
                Total Connected Accounts
              </h4>
              <p className="text-2xl font-bold text-blue-900">
                {dspList.length}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-blue-600">
                Across{" "}
                {
                  SUPPORTED_DSPS.filter(
                    (dsp) => getConnectedAccounts(dsp).length > 0
                  ).length
                }{" "}
                platforms
              </p>
              <p className="text-xs text-blue-600">
                Data syncing automatically
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add DSP Modal */}
      <AddDSPModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddDSP}
        dspData={null}
        selectedDSP={selectedDsp}
      />

      {/* Edit DSP Modal */}
      <EditDSPModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditDsp}
        dspData={
          currentDsp || {
            id: "",
            type: "",
            name: "",
            email: "",
            password: "",
            createdAt: "",
          }
        }
      />
    </div>
  );
};

export default DSPs;
